import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Tooltip from 'components/Tooltip';
import { riskThresholdsService, batchesService } from '../../../services/metalguard';

const DEFAULT_THRESHOLDS = {
  exposure_warning_eur: 50000,
  exposure_critical_eur: 100000,
  max_concentration_pct: 40,
  inventory_age_alert_days: 30,
  min_margin_alert_eur_per_ton: 50,
};

function ThresholdField({ label, description, value, onChange, unit, min, max, step = 1, currentValue, icon }) {
  const getStatus = () => {
    if (currentValue === undefined || currentValue === null) return null;
    if (currentValue >= value) return 'critical';
    if (currentValue >= value * 0.8) return 'warning';
    return 'ok';
  };
  const status = getStatus();
  const statusConfig = {
    ok: { color: 'text-success', bg: 'bg-success/10 border-success/20', icon: 'CheckCircle', label: 'Normal' },
    warning: { color: 'text-warning', bg: 'bg-warning/10 border-warning/20', icon: 'AlertTriangle', label: 'Atencion' },
    critical: { color: 'text-error', bg: 'bg-error/10 border-error/20', icon: 'AlertCircle', label: 'Critico' },
  };
  const pct = currentValue != null ? Math.min(100, (currentValue / value) * 100) : 0;
  const barColor = status === 'ok' ? 'bg-success' : status === 'warning' ? 'bg-warning' : 'bg-error';
  const handleChange = (e) => { const v = parseFloat(e?.target?.value); if (!isNaN(v)) onChange(v); };
  const error = value < min ? `El valor minimo es ${min} ${unit}` : (max !== undefined && value > max ? `El valor maximo es ${max} ${unit}` : null);

  return (
    <div className="p-5 bg-card border border-border rounded-lg">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0" style={{ background: 'var(--color-muted)' }}>
            <Icon name={icon} size={18} color="var(--color-accent)" strokeWidth={2} />
          </div>
          <div>
            <p className="font-semibold text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.9375rem' }}>{label}</p>
            <p className="text-muted-foreground mt-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>{description}</p>
          </div>
        </div>
        {status && (
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium flex-shrink-0 ${statusConfig?.[status]?.bg} ${statusConfig?.[status]?.color}`} style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            <Icon name={statusConfig?.[status]?.icon} size={12} color="currentColor" strokeWidth={2.5} />
            {statusConfig?.[status]?.label}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-1">
          <input type="number" value={value} onChange={handleChange} min={min} max={max} step={step}
            className={`w-full bg-background border rounded-md px-3 py-2 text-foreground focus:outline-none focus:border-accent transition-colors ${error ? 'border-error' : 'border-border'}`}
            style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.9375rem', fontVariantNumeric: 'tabular-nums' }} />
          {error && <p className="text-error mt-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>{error}</p>}
        </div>
        <span className="text-muted-foreground font-medium flex-shrink-0" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>{unit}</span>
      </div>
      {currentValue != null && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.75rem' }}>
              Valor actual: <span className={`font-semibold ${statusConfig?.[status]?.color}`} style={{ fontFamily: '"JetBrains Mono", monospace', fontVariantNumeric: 'tabular-nums' }}>{currentValue?.toLocaleString('es-ES')} {unit}</span>
            </span>
            <span className="text-muted-foreground" style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>{pct?.toFixed(0)}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

export default function RiskThresholds({ onRegisterSave, onToast }) {
  const [thresholds, setThresholds] = useState(DEFAULT_THRESHOLDS);
  const [currentValues, setCurrentValues] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const [threshRes, batchesRes] = await Promise.all([
        riskThresholdsService?.get(),
        batchesService?.getAll(),
      ]);
      if (threshRes?.data) {
        setThresholds({
          exposure_warning_eur: parseFloat(threshRes?.data?.exposure_warning_eur),
          exposure_critical_eur: parseFloat(threshRes?.data?.exposure_critical_eur),
          max_concentration_pct: parseFloat(threshRes?.data?.max_concentration_pct),
          inventory_age_alert_days: threshRes?.data?.inventory_age_alert_days,
          min_margin_alert_eur_per_ton: parseFloat(threshRes?.data?.min_margin_alert_eur_per_ton),
        });
      }
      // Compute current values from batches
      if (batchesRes?.data) {
        const active = batchesRes?.data?.filter(b => ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status));
        const totalExp = active?.reduce((s, b) => s + (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0), 0);
        const byMetal = {};
        active?.forEach(b => { const m = b?.metal_name || 'Otros'; byMetal[m] = (byMetal?.[m] || 0) + (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0); });
        const maxConc = totalExp > 0 ? Math.max(...Object.values(byMetal)?.map(v => (v / totalExp) * 100)) : 0;
        const minMargin = active?.length > 0 ? Math.min(...active?.filter(b => b?.expected_margin != null)?.map(b => parseFloat(b?.expected_margin) * (parseFloat(b?.purchase_price) || 0) / 100)) : 0;
        setCurrentValues({ total_exposure_eur: totalExp, max_concentration_pct: maxConc, oldest_inventory_days: 22, min_margin_eur_per_ton: minMargin });
      }
      setLoading(false);
    }
    load();
  }, []);

  useEffect(() => {
    if (!onRegisterSave) return;
    onRegisterSave(() => async () => {
      const { error } = await riskThresholdsService?.upsert(thresholds);
      if (error) throw new Error(error.message);
    });
  }, [thresholds, onRegisterSave]);

  const update = (field) => (value) => setThresholds((prev) => ({ ...prev, [field]: value }));

  const fields = [
    { key: 'exposure_warning_eur', label: 'Nivel de Alerta de Exposicion', description: 'Umbral de advertencia para la exposicion total al mercado', tooltipDesc: 'Exposición total en € a partir de la cual el sistema emite una alerta de advertencia (amarilla) en el Dashboard.', unit: '€', min: 1000, step: 1000, icon: 'AlertTriangle', currentValue: currentValues?.total_exposure_eur },
    { key: 'exposure_critical_eur', label: 'Nivel Critico de Exposicion', description: 'Umbral critico que activa alertas de maxima prioridad', tooltipDesc: 'Exposición total en € a partir de la cual el sistema emite una alerta crítica (roja). Indica concentración de riesgo elevada.', unit: '€', min: 1000, step: 1000, icon: 'AlertCircle', currentValue: currentValues?.total_exposure_eur },
    { key: 'max_concentration_pct', label: 'Concentracion Maxima por Metal', description: 'Porcentaje maximo del inventario en un solo metal', tooltipDesc: 'Porcentaje máximo del total de exposición que puede concentrarse en un solo metal. Superar este límite genera alerta de concentración.', unit: '%', min: 5, max: 100, step: 1, icon: 'PieChart', currentValue: currentValues?.max_concentration_pct },
    { key: 'inventory_age_alert_days', label: 'Alerta de Antiguedad de Inventario', description: 'Dias maximos antes de alertar sobre lotes sin movimiento', tooltipDesc: 'Número de días a partir del cual un lote en inventario genera alerta por antigüedad. Lotes viejos aumentan el riesgo de mercado.', unit: 'dias', min: 1, max: 365, step: 1, icon: 'Clock', currentValue: currentValues?.oldest_inventory_days },
    { key: 'min_margin_alert_eur_per_ton', label: 'Alerta de Margen Minimo', description: 'Margen minimo por tonelada antes de activar alerta de rentabilidad', tooltipDesc: 'Margen mínimo en €/t por debajo del cual el sistema alerta. Ayuda a detectar lotes con rentabilidad comprometida.', unit: '€/t', min: 0, step: 5, icon: 'TrendingDown', currentValue: currentValues?.min_margin_eur_per_ton },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex items-center gap-3">
          <Icon name="Loader2" size={22} color="var(--color-accent)" strokeWidth={2} className="animate-spin" />
          <span className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Cargando umbrales...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-foreground mb-1" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.125rem', fontWeight: '600' }}>Umbrales de Riesgo</h3>
        <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}>Configura los niveles de alerta para exposicion, concentracion y margenes. Los indicadores muestran el estado actual del sistema.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {fields?.map((field) => (
          <ThresholdField
            key={field?.key}
            label={
              <Tooltip title={field?.label} description={field?.tooltipDesc}>
                {field?.label}
              </Tooltip>
            }
            description={field?.description}
            value={thresholds?.[field?.key]}
            onChange={update(field?.key)}
            unit={field?.unit}
            min={field?.min}
            max={field?.max}
            step={field?.step}
            icon={field?.icon}
            currentValue={field?.currentValue}
          />
        ))}
      </div>
      <div className="mt-6 p-4 bg-muted rounded-lg border border-border">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={16} color="var(--color-accent)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
          <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>Los umbrales de exposicion critica deben ser superiores a los de alerta. Las alertas se evaluan en tiempo real y se muestran en la barra de navegacion.</p>
        </div>
      </div>
    </div>
  );
}
